import { Dialog, DialogProps } from '../ui';

export interface UnsavedChangesModalProps extends DialogProps {
  onDiscard?: () => void;
  onSave?: () => void;
  savingInProgress?: boolean;
}

export const UnsavedChangesModal = ({
  onClose,
  onDiscard,
  onSave,
  savingInProgress,
  ...props
}: UnsavedChangesModalProps) => {
  const handleSave = () => {
    if (onSave) {
      try {
        onSave();
      } catch (error) {
        // Error handling - onSave should handle its own errors
      }
    } else {
      // Fallback: try to submit the form directly
      const form = document.querySelector('form');
      if (form) {
        try {
          form.requestSubmit();
        } catch (error) {
          // If requestSubmit fails, try to manually trigger the submit event
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      } else {
        // Try to find form in the modal container
        const modalContainer = document.querySelector('[role="dialog"]');
        if (modalContainer) {
          const formInModal = modalContainer.querySelector('form');
          if (formInModal) {
            formInModal.requestSubmit();
          }
        }
      }
    }
  };

  const handleDiscard = () => {
    if (onDiscard) {
      onDiscard();
    }
  };

  return (
    <Dialog
      onClose={onClose}
      closeOnOutsideClick
      className="shadow-modal backdrop:bg-modalBackdropColor !m-auto w-full max-w-md rounded-[12px] bg-white"
      {...props}
    >
      <div className="flex flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h2 className="font-['Open_Sans'] text-xl font-semibold leading-7 text-black">Unsaved changes</h2>
          <p className="text-section-header-slate font-['Open_Sans'] text-sm font-normal leading-6">
            If you leave this page, any changes you have made will be lost.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={savingInProgress}
            className="bg-button-primary hover:bg-button-primary-hover inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-6 py-2.5 font-['Open_Sans'] text-base font-medium leading-5 text-white transition-colors disabled:opacity-50"
            data-testid="unsaved-changes-modal-save-button"
          >
            {savingInProgress ? (
              <span className="flex items-center gap-2">
                <svg
                  className="h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </span>
            ) : (
              'Save'
            )}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            disabled={savingInProgress}
            className="bg-button-secondary text-button-primary hover:bg-button-secondary-hover inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-6 py-2.5 font-['Open_Sans'] text-base font-medium leading-5 transition-colors disabled:opacity-50"
            data-testid="unsaved-changes-modal-discard-button"
          >
            Discard
          </button>
        </div>
      </div>
    </Dialog>
  );
};
